import { API_GENAI_SERVER_URL } from 'js/constants/api';
import { GenAITransformInputContext } from 'js/components/recipeBuilder/GenAiTransformButton/GenAITransformContext.types';

import { createGenAiPayload } from '../utils';

/**
 * Source of truth for the endpoints. Do not use magic strings.
 * This is used to determine the endpoint to use for the streamFetch.
 * Some validation in GenAITransformProvider is based on these values.
 */
export const GEN_AI_ENDPOINTS = Object.freeze({
  GENAI_TX_DSL: 'genai_dsl_transform',
  GENAI_CODEGEN: 'genai_codegen',
  GENAI_TRANSFORM: 'genai_transform',
});

export type StreamFetchParams = {
  type: string;
  endpoint: string;
  input: string;
  onData: (chunk: string) => void;
  conversationId: number;
  nexsetId: string;
  token: string;
  codeType?: string;
  context?: GenAITransformInputContext;
};

async function streamFetch(props: StreamFetchParams) {
  const { endpoint, onData } = props;

  try {
    const payload = createGenAiPayload(props);
    const response = await fetch(endpoint, payload);

    if (!response.ok) {
      const errorPayload = await response.json();
      throw new Error(`Error: ${response.status} ${errorPayload.error}`);
    }

    if (!response.body) throw new Error('ReadableStream not supported in this browser.');

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      onData(chunk);
    }
  } catch (error) {
    console.error('Error in streamFetch:', error);
    throw error;
  }
}

type SendMessageParams = Pick<StreamFetchParams, 'endpoint' | 'input' | 'nexsetId' | 'context'> & {
  user: any;
  selectedCodeTypeOption: any;
  onSuccess: (chunk: string) => void;
  onError: (error: string) => void;
  onComplete: () => void;
};

export const sendMessage = async ({
  input,
  endpoint,
  nexsetId,
  user,
  selectedCodeTypeOption,
  onSuccess,
  onError,
  onComplete,
  context,
}: SendMessageParams) => {
  const selectCodeType = selectedCodeTypeOption?.value || 'python';

  const conversationId = Number(nexsetId);

  try {
    await streamFetch({
      type: endpoint,
      endpoint: `${API_GENAI_SERVER_URL}/${endpoint}`,
      input,
      onData: onSuccess,
      conversationId,
      nexsetId,
      token: user.token,
      codeType: selectCodeType,
      context,
    });
  } catch (error) {
    onError(error.message);
  } finally {
    onComplete();
  }
};
